/**
 * Lesson curriculum — original teaching content written for this project, in
 * both Chinese and English. The UI shows whichever language the user selected.
 *
 * Every position and solution here is verified programmatically before release
 * by `scripts/verify-lessons.ts`, so a lesson can never ship with an illegal
 * solution or an impossible task.
 */

export interface Bi {
  zh: string;
  en: string;
  /** Vietnamese. Optional: pick() falls back to English without it. */
  vi?: string;
}

export type Task =
  | { kind: "info" }
  | { kind: "any-legal" }
  | { kind: "play-move"; solution: string[] }
  | { kind: "achieve-mate" };

/**
 * A tailored answer to a wrong move that is worth answering specifically —
 * the near-miss everybody tries, the plausible move that loses a piece.
 *
 * These are for moves where a *specific* explanation teaches more than the
 * engine's generic one. Everything not listed still gets an explanation: the
 * lesson view derives one from the position itself, so a learner is never told
 * only "wrong".
 */
export interface Miss {
  uci: string;
  say: Bi;
}

/** A check of understanding. Exactly one option is correct. */
export interface Quiz {
  q: Bi;
  options: { text: Bi; ok?: boolean; why: Bi }[];
}

/**
 * One beat of a lesson: a position, something to read, and something to do.
 *
 * Splitting a lesson into steps is what turns it from a paragraph with a board
 * next to it into teaching — show the idea, then have the learner perform it,
 * then test whether it stuck.
 */
export interface Step {
  /** Defaults to the lesson's own FEN. */
  fen?: string;
  focus?: string;
  text: Bi;
  task: Task;
  success?: Bi;
  misses?: Miss[];
}

/** Where a lesson sits in the curriculum. */
export type Stage = "beginner" | "intermediate" | "advanced";

export interface Lesson {
  id: string;
  group: Bi;
  title: Bi;
  /** The opening position, and the fallback for any step that omits one. */
  fen: string;
  focus?: string;
  orientation?: "red" | "black";
  text: Bi;
  task: Task;
  success?: Bi;
  stage?: Stage;
  /** Roughly how long this takes, in minutes. Shown on the lesson cards. */
  minutes?: number;
  /** Everything after the opening beat. The lesson's own fields are step one. */
  steps?: Step[];
  /** Specific answers to specific wrong moves on the opening beat. */
  misses?: Miss[];
  /** Asked once the board work is done. */
  quiz?: Quiz;
  /** Lesson ids worth doing next. */
  next?: string[];
}

/**
 * Lesson text edited in the admin console, laid over the catalogue above.
 *
 * The console cannot store a lesson the engine disagrees with: every save is
 * verified server-side against @xq/rules before it is written, so anything
 * that arrives here has already been checked at least as strictly as the
 * build-time pass over this file. A failed or missing fetch simply leaves the
 * built-in catalogue in place, which is always a complete, valid curriculum.
 */
export async function applyLessonOverrides(): Promise<void> {
  try {
    const res = await fetch("/api/lessons/overrides");
    if (!res.ok) return;
    const body = (await res.json()) as { overrides?: Record<string, Record<string, unknown>> };
    for (const [id, patch] of Object.entries(body.overrides ?? {})) {
      const lesson = LESSONS.find((l) => l.id === id);
      // Mutated in place: the array is shared by every screen that reads it.
      if (lesson && patch) Object.assign(lesson, patch);
    }
  } catch {
    /* the built-in catalogue is a perfectly good fallback */
  }
}

/** Every beat of a lesson, with step one synthesised from the lesson itself. */
export function stepsOf(l: Lesson): Step[] {
  const first: Step = {
    fen: l.fen,
    text: l.text,
    task: l.task,
    ...(l.focus === undefined ? {} : { focus: l.focus }),
    ...(l.success === undefined ? {} : { success: l.success }),
    ...(l.misses === undefined ? {} : { misses: l.misses }),
  };
  return [first, ...(l.steps ?? [])];
}

const G_BASICS: Bi = { zh: "基础走法", en: "Piece Basics", vi: "Cách đi các quân" };
const G_RULES: Bi = { zh: "规则", en: "Core Rules", vi: "Luật cơ bản" };
const G_TACTICS: Bi = { zh: "战术", en: "Tactics", vi: "Chiến thuật" };
const G_ENDGAME: Bi = { zh: "残局", en: "Endgame", vi: "Tàn cuộc" };
const G_OPENING: Bi = { zh: "开局", en: "Openings", vi: "Khai cuộc" };

export const LESSONS: Lesson[] = [
  // ---------------------------------------------------------------- Basics
  {
    id: "b-rook",
    group: G_BASICS,
    title: { zh: "车 · 直来直往", en: "The Chariot (Rook)", vi: "Quân Xe" },
    fen: "3k5/9/9/9/9/4R4/9/9/9/4K4 w - - 0 1",
    focus: "e4",
    text: {
      zh: "车是象棋中最强的子力。它沿直线（横或竖）任意距离移动，吃掉路线上遇到的第一个敌子，中间不能有任何棋子阻挡。高亮的点表示这只车能到达的所有位置。",
      en: "车 (chariot) is the strongest piece. It slides any distance along a rank or file and captures the first enemy piece it meets. Nothing may be jumped over. The highlighted dots show every square it can reach.",
      vi: "车 (Xe) là quân mạnh nhất. Xe đi bao xa tuỳ ý theo hàng ngang hoặc lộ dọc và ăn quân địch đầu tiên nó gặp. Không được nhảy qua quân nào. Các chấm sáng là mọi điểm nó có thể tới.",
    },
    task: { kind: "info" },
  },
  {
    id: "b-horse",
    group: G_BASICS,
    title: { zh: "马 · 走日", en: "The Horse (Knight)", vi: "Quân Mã" },
    fen: "3k5/9/9/9/9/4N4/9/9/9/4K4 w - - 0 1",
    focus: "e4",
    text: {
      zh: "马走「日」字：先直行一步，再斜行一步。与国际象棋不同，中国象棋的马不能跳过棋子——它会被「蹩腿」。在空旷处，马有八个可去的点。",
      en: "马 (horse) moves one point straight, then one diagonally outward — the shape of 日. Unlike a western knight it does NOT jump: it can be blocked. From an open square it has eight destinations.",
      vi: "马 (Mã) đi một ô thẳng rồi một ô chéo ra ngoài — theo hình chữ 日. Khác Mã cờ vua, nó KHÔNG nhảy qua quân: nó có thể bị cản. Ở chỗ thoáng, Mã có tám điểm có thể tới.",
    },
    task: { kind: "info" },
  },
  {
    id: "b-horse-block",
    group: G_BASICS,
    title: { zh: "蹩马腿 · 别住马脚", en: "Hobbling the Horse's Leg", vi: "Cản chân Mã" },
    fen: "3k5/9/9/9/4P4/4N4/9/9/9/4K4 w - - 0 1",
    focus: "e4",
    text: {
      zh: "这是最容易让人意外的规则。紧贴马的那一格若有子，该方向的走法就被封住，称为「蹩马腿」。正前方的兵挡住了马腿，使前方两个点都走不了。数一数：只剩六个点。",
      en: "This is the rule that surprises chess players. A single piece directly beside the horse blocks the moves in that direction — 蹩马腿. The pawn straight ahead removes BOTH forward destinations. Count the dots: only six remain.",
      vi: "Đây là luật làm người chơi cờ vua bất ngờ. Chỉ một quân đứng sát bên Mã cũng chặn các nước đi theo hướng đó — 蹩马腿 (cản chân Mã). Con Tốt ngay phía trước chặn CẢ HAI điểm phía trước. Đếm các chấm: chỉ còn sáu.",
    },
    task: { kind: "info" },
  },
  {
    id: "b-cannon",
    group: G_BASICS,
    title: { zh: "炮 · 隔山打牛", en: "The Cannon", vi: "Quân Pháo" },
    fen: "3k5/4r4/9/4p4/9/4C4/9/9/9/4K4 w - - 0 1",
    focus: "e4",
    text: {
      zh: "炮不吃子时走法与车相同；但要吃子必须隔着一个棋子跳过去，这个棋子称为「炮架」，敌我皆可。图中黑卒就是炮架，炮可以越过它吃掉后面的车。",
      en: "炮 (cannon) moves like a chariot when NOT capturing — but to capture it must jump exactly one piece (the 炮架, screen) of either colour. Here the black pawn is the screen, so the cannon can leap it and take the chariot behind.",
      vi: "炮 (Pháo) đi như Xe khi KHÔNG ăn quân — nhưng muốn ăn thì phải nhảy qua đúng một quân (炮架, ngòi) của bên nào cũng được. Ở đây Tốt đen là ngòi, nên Pháo nhảy qua nó và ăn Xe phía sau.",
    },
    task: { kind: "info" },
  },
  {
    id: "b-elephant",
    group: G_BASICS,
    title: { zh: "相 · 不过河", en: "The Elephant", vi: "Quân Tượng" },
    fen: "3k5/9/9/9/9/9/9/4B4/9/4K4 w - - 0 1",
    focus: "e2",
    text: {
      zh: "相（象）走「田」字，斜走两格，且永远不能过河，是纯粹的防守子力。若「田」字中心有子挡住就走不了，称为「塞象眼」。",
      en: "相/象 (elephant) moves exactly two points diagonally and may never cross the river — it is a pure defender. It is blocked if the midpoint of its path is occupied (塞象眼).",
      vi: "相/象 (Tượng) đi chéo đúng hai ô và không bao giờ được qua sông — một quân thuần phòng thủ. Nó bị chặn nếu điểm giữa đường đi có quân (塞象眼, cản mắt Tượng).",
    },
    task: { kind: "info" },
  },
  {
    id: "b-advisor",
    group: G_BASICS,
    title: { zh: "仕 · 九宫之内", en: "The Advisor", vi: "Quân Sĩ" },
    fen: "3k5/9/9/9/9/9/9/9/4A4/4K4 w - - 0 1",
    focus: "e1",
    text: {
      zh: "仕（士）每次斜走一格，并且永远不能走出九宫——棋盘上画有交叉斜线的三格见方区域。它与相一起构成帅的屏障。",
      en: "仕/士 (advisor) moves one point diagonally and can never leave the 九宫 (palace) — the 3×3 box marked with diagonals. Together with the elephants it forms the king's shield.",
      vi: "仕/士 (Sĩ) đi chéo một ô và không bao giờ rời 九宫 (cung) — khu 3×3 có vẽ đường chéo. Cùng hai Tượng, Sĩ tạo thành lá chắn cho Tướng.",
    },
    task: { kind: "info" },
  },
  {
    id: "b-pawn",
    group: G_BASICS,
    title: { zh: "兵 · 过河威力大", en: "The Pawn", vi: "Quân Tốt" },
    fen: "3k5/9/9/4P4/9/9/4P4/9/9/4K4 w - - 0 1",
    focus: "e6",
    text: {
      zh: "兵（卒）每次向前一格，永不后退。过河之前只能直走；过河之后还可以左右平移——所以过了河的兵价值大得多。象棋没有升变。",
      en: "兵/卒 (pawn) moves one point forward and never backward. Before crossing the river that is its ONLY move. After crossing it may also move sideways — which is why a crossed pawn is worth far more. There is no promotion in xiangqi.",
      vi: "兵/卒 (Tốt) tiến một ô và không bao giờ lùi. Trước khi qua sông, đó là nước đi DUY NHẤT của nó. Qua sông rồi thì đi ngang được — vì thế Tốt qua sông đáng giá hơn nhiều. Cờ tướng không có phong cấp.",
    },
    task: { kind: "info" },
  },
  {
    id: "b-king",
    group: G_BASICS,
    title: { zh: "帅 · 白脸将", en: "The King & Flying General", vi: "Tướng & lộ mặt tướng" },
    fen: "4k4/9/9/9/9/9/9/9/9/3K5 w - - 0 1",
    focus: "d0",
    text: {
      zh: "帅（将）每次直走一格，且只能在九宫内活动。特别规则：双方的帅与将不能在同一条直线上直接照面，称为「白脸将」。注意图中这个帅不能走到中路，否则就与黑将照面了。",
      en: "帅/将 (king) moves one point orthogonally, only inside the palace. The special rule: the two kings may NEVER face each other down an open file — 白脸将. Notice this king cannot step to the centre file, because that would put it eye-to-eye with the black king.",
      vi: "帅/将 (Tướng) đi ngang hoặc dọc một ô, chỉ trong cung. Luật đặc biệt: hai Tướng KHÔNG BAO GIỜ được đối mặt nhau trên một lộ trống — 白脸将 (lộ mặt tướng). Để ý Tướng này không thể bước vào lộ giữa, vì như vậy nó sẽ đối mặt Tướng đen.",
    },
    task: { kind: "info" },
  },

  // ---------------------------------------------------------------- Rules
  {
    id: "r-check",
    group: G_RULES,
    title: { zh: "将军 · 应将", en: "Check & Escaping It", vi: "Chiếu & cách đỡ chiếu" },
    fen: "4k4/9/9/9/4r4/9/9/9/9/4K4 w - - 0 1",
    text: {
      zh: "黑车沿直线攻击你的帅——这就是「将军」。你必须立即应将：走帅、垫子挡住，或吃掉攻击的子。走任意一步合法棋来解将。",
      en: "The black chariot attacks your king down the open file — 将军 (check). You must answer it immediately: move the king, block the line, or capture the attacker. Play any legal move to escape.",
      vi: "Xe đen tấn công Tướng của bạn dọc lộ trống — 将军 (chiếu). Bạn phải đỡ ngay: đi Tướng, chắn đường, hoặc ăn quân đang chiếu. Đi một nước hợp lệ bất kỳ để thoát.",
    },
    task: { kind: "any-legal" },
    success: {
      zh: "正确——帅安全了。任何会让自己被将军的走法都是非法的，根本走不出来。",
      en: "Correct — the king is safe. A move that leaves your king in check is simply illegal and cannot be played.",
      vi: "Đúng rồi — Tướng đã an toàn. Nước nào để Tướng mình bị chiếu đều không hợp lệ và không thể đi.",
    },
  },
  {
    id: "r-mate",
    group: G_RULES,
    title: { zh: "将死 · 一步杀", en: "Checkmate in One", vi: "Chiếu bí một nước" },
    fen: "4k4/9/9/9/9/9/9/R8/3R1R3/3K5 w - - 0 1",
    text: {
      zh: "你在 d 线和 f 线上的车已经控制住黑将的逃跑格。请沿中路一步将死对方。",
      en: "Your chariots on the d- and f-files already cover the black king's escape squares. Deliver checkmate in one move by attacking down the centre file.",
      vi: "Hai Xe của bạn ở lộ d và lộ f đã khống chế các điểm thoát của Tướng đen. Hãy chiếu bí trong một nước bằng cách tấn công dọc lộ giữa.",
    },
    task: { kind: "achieve-mate" },
    success: {
      zh: "将死！对方被将军且无路可走，棋局结束。",
      en: "将死! The king is attacked and has no legal reply — that ends the game.",
      vi: "将死! Tướng bị chiếu và không còn nước đỡ hợp lệ — ván cờ kết thúc.",
    },
  },
  {
    id: "r-stalemate",
    group: G_RULES,
    title: { zh: "困毙 · 无棋可走即负", en: "Stalemate is a LOSS", vi: "Hết nước đi là THUA" },
    fen: "4k4/9/4P4/9/9/9/9/9/3R1R3/K8 b - - 0 1",
    orientation: "red",
    text: {
      zh: "轮到黑方走棋——黑方没有被将军，却一步合法棋也走不出来。在国际象棋中这是和棋，但在中国象棋中这叫「困毙」：无棋可走的一方判负。这一条差异彻底改变了残局的下法。",
      en: "Black to move — and Black is NOT in check, yet has no legal move at all. In western chess this is a draw. In xiangqi it is 困毙: the player with no legal move LOSES. This single difference changes endgame technique completely.",
      vi: "Đen đi — Đen KHÔNG bị chiếu, nhưng không còn nước hợp lệ nào. Trong cờ vua đây là hoà. Trong cờ tướng đó là 困毙 (hết nước đi): bên không còn nước hợp lệ sẽ THUA. Chỉ một khác biệt này đã thay đổi hoàn toàn kỹ thuật tàn cuộc.",
    },
    task: { kind: "info" },
  },

  // ---------------------------------------------------------------- Tactics
  {
    id: "t-fork-horse",
    group: G_TACTICS,
    title: { zh: "马双捉 · 一子两吃", en: "The Horse Fork", vi: "Mã bắt đôi" },
    fen: "4k4/9/9/2r3r2/9/9/3N5/9/9/4K4 w - - 0 1",
    text: {
      zh: "两只黑车都没有保护，且在同一横线上。找出那步能同时攻击两只车的马步——下一步你就能吃掉其中一只。",
      en: "Two black chariots sit undefended on the same rank. Find the horse move that attacks BOTH at once — next move you win one of them.",
      vi: "Hai Xe đen không được bảo vệ, cùng nằm trên một hàng ngang. Tìm nước Mã tấn công CẢ HAI cùng lúc — nước sau bạn ăn được một con.",
    },
    task: { kind: "play-move", solution: ["d3e5"] },
    success: {
      zh: "双捉！一步马，两只车同时被攻击，黑方只能救一只。",
      en: "双捉! One horse move, two chariots attacked, and Black can only save one.",
      vi: "双捉! Một nước Mã, hai Xe bị tấn công, và Đen chỉ cứu được một.",
    },
  },
  {
    id: "t-fork-rook",
    group: G_TACTICS,
    title: { zh: "车捉双 · 抢占要道", en: "The Chariot Double Attack", vi: "Xe bắt đôi" },
    fen: "4k4/9/9/9/1n5c1/9/9/9/4R4/4K4 w - - 0 1",
    text: {
      zh: "黑马和黑炮都无根（没有保护），且在同一横线上。把你的车开到能同时攻击它们的那条线上。",
      en: "The black horse and cannon are both loose on the same rank. Bring your chariot to the rank where it attacks both at the same time.",
      vi: "Mã và Pháo đen đều không được bảo vệ, cùng nằm trên một hàng ngang. Đưa Xe tới hàng đó để tấn công cả hai cùng lúc.",
    },
    task: { kind: "play-move", solution: ["e1e5"] },
    success: {
      zh: "很好——占据空的直线或横线，正是车凭空制造威胁的方法。",
      en: "Excellent — occupying an open rank or file is how a chariot creates threats out of nothing.",
      vi: "Tuyệt vời — chiếm một hàng hay lộ trống là cách Xe tạo ra đòn doạ từ con số không.",
    },
  },
  {
    id: "t-general-pin",
    group: G_TACTICS,
    title: { zh: "白脸将牵制", en: "Pinned by the Flying General", vi: "Bị ghim vì lộ mặt tướng" },
    fen: "4k4/9/9/9/4n4/9/9/9/9/4K4 b - - 0 1",
    focus: "e5",
    text: {
      zh: "轮到黑方走棋——但中路这只马动弹不得。它一旦离开，双方帅将就会照面，这是不允许的。「白脸将」规则形成了永久的牵制：注意这只马一步合法棋都没有。",
      en: "Black to move — but the horse on the centre file is frozen. If it steps aside the two kings would face each other, which is illegal. The flying-general rule acts as a permanent pin: notice the horse has ZERO legal moves.",
      vi: "Đen đi — nhưng con Mã ở lộ giữa không thể nhúc nhích. Nếu nó rời đi, hai Tướng sẽ đối mặt nhau, mà điều đó bị cấm. Luật lộ mặt tướng tạo thành một thế ghim vĩnh viễn: để ý Mã KHÔNG có nước hợp lệ nào.",
    },
    task: { kind: "info" },
  },

  // ---------------------------------------------------------------- Endgame
  {
    id: "e-two-rooks",
    group: G_ENDGAME,
    title: { zh: "双车错 · 二车逼杀", en: "Two Chariots Mate", vi: "Hai Xe chiếu bí" },
    fen: "3k5/9/9/9/9/9/9/9/R3R4/4K4 w - - 0 1",
    text: {
      zh: "经典的「双车错」杀法。你在 e 线的车已经守住了将唯一的横向逃路。把另一只车开到将所在的直线上——一步将死。",
      en: "The classic 双车错 (two chariots) finish. Your chariot on the e-file already guards the king's only sideways escape. Bring the other chariot onto the file the king stands on — checkmate in one.",
      vi: "Đòn kết thúc kinh điển 双车错 (song xa thác). Xe của bạn ở lộ e đã chặn đường thoát ngang duy nhất của Tướng. Đưa Xe còn lại vào lộ Tướng đang đứng — chiếu bí một nước.",
    },
    task: { kind: "achieve-mate" },
    success: {
      zh: "双车错！两只车分别控制直线与横线，将无处可逃。",
      en: "双车错! The two chariots cover both lines and the king has nowhere to go.",
      vi: "双车错! Hai Xe khống chế cả hai đường, Tướng không còn chỗ chạy.",
    },
  },

  // ---------------------------------------------------------------- Openings
  {
    id: "o-central-cannon",
    group: G_OPENING,
    title: { zh: "中炮 · 当头炮", en: "The Central Cannon", vi: "Pháo đầu" },
    fen: "rnbakabnr/9/1c5c1/p1p1p1p1p/9/9/P1P1P1P1P/1C2C4/9/RNBAKABNR b - - 1 1",
    text: {
      zh: "一、炮二平五——「当头炮」，象棋中最流行也最具攻击性的开局。炮移到中路，直指对方将门，并以己方兵作为将来的炮架。",
      en: "1. 炮二平五 — the Central Cannon, the most popular and most aggressive first move in xiangqi. The cannon swings to the centre file and immediately aims at the black king, using the pawns as future screens.",
      vi: "1. 炮二平五 (Pháo 2 bình 5) — Pháo đầu, nước mở đầu phổ biến nhất và giàu tính tấn công nhất trong cờ tướng. Pháo chuyển vào lộ giữa và lập tức nhắm vào Tướng đen, dùng các Tốt làm ngòi về sau.",
    },
    task: { kind: "info" },
  },
  {
    id: "o-screen-horses",
    group: G_OPENING,
    title: { zh: "屏风马 · 稳健应对", en: "The Screen Horses Defence", vi: "Bình phong Mã" },
    fen: "rnbakab1r/9/1c4nc1/p1p1p1p1p/9/9/P1P1P1P1P/1C2C4/9/RNBAKABNR w - - 2 2",
    orientation: "black",
    text: {
      zh: "一…马8进7——「屏风马」。黑方跳马保护中卒，计划把两只马并排摆成屏风的形状。这是应对中炮最稳健、最经典的下法。",
      en: "1... 马8进7 — the Screen Horses. Black develops a horse to guard the centre pawn, planning to place both horses side by side like a folding screen (屏风). It is the soundest, most classical answer to the Central Cannon.",
      vi: "1... 马8进7 (Mã 8 tiến 7) — Bình phong Mã. Đen lên Mã để giữ Tốt đầu, định xếp hai Mã cạnh nhau như một tấm bình phong (屏风). Đây là cách đáp vững chắc và kinh điển nhất trước Pháo đầu.",
    },
    task: { kind: "info" },
  },

  // ================================================================ new work
  // These are built the way the whole curriculum is heading: read the idea,
  // perform it on the board, then answer for it. Wrong moves get a specific
  // reply where one teaches more than the engine's generic explanation.

  {
    id: "b-cannon-screen",
    group: G_BASICS,
    stage: "beginner",
    minutes: 4,
    title: { zh: "炮 · 架与打", en: "The Cannon Needs a Screen", vi: "Pháo cần ngòi" },
    fen: "3k5/4r4/9/4p4/9/4C4/9/9/9/4K4 w - - 0 1",
    focus: "e4",
    text: {
      zh: "炮走直线，与车相同；但吃子时必须隔着「炮架」——正好一个棋子。这里 e6 的黑卒就是炮架，e8 的黑车是目标。注意：炮吃不到紧挨着它的子。",
      en: "The cannon moves in straight lines exactly like a chariot. Capturing is the different rule: there must be exactly one piece — the screen — between the cannon and what it takes. Here the black soldier on e6 is the screen and the chariot on e8 is the target. Note that the cannon cannot take the soldier itself: there is nothing in between.",
      vi: "Pháo đi theo đường thẳng y như Xe. Điểm khác là khi ăn quân: giữa Pháo và quân bị ăn phải có đúng một quân — gọi là ngòi. Ở đây Tốt đen ở e6 là ngòi, còn Xe ở e8 là mục tiêu. Lưu ý Pháo không ăn được chính con Tốt: giữa chúng không có quân nào.",
    },
    task: { kind: "play-move", solution: ["e4e8"] },
    misses: [
      {
        uci: "e4e5",
        say: {
          zh: "这是合法的移动，但只是走到空点，并没有吃子。再看看隔着炮架能打到什么。",
          en: "Legal — a cannon moves to an empty point freely. But it captures nothing. Look again for what it can reach *through* the screen.",
          vi: "Hợp lệ — Pháo được tự do đi tới điểm trống. Nhưng nó không ăn được gì. Hãy tìm xem nó với tới được gì *qua* ngòi.",
        },
      },
    ],
    success: {
      zh: "正确。炮越过 e6 的卒，吃掉 e8 的车。",
      en: "Correct. The cannon jumped the soldier on e6 and took the chariot behind it.",
      vi: "Chính xác. Pháo nhảy qua Tốt ở e6 và ăn Xe phía sau.",
    },
    quiz: {
      q: { zh: "炮要吃子，中间需要几个棋子？", en: "How many pieces must sit between a cannon and the piece it captures?", vi: "Giữa Pháo và quân bị ăn phải có bao nhiêu quân?" },
      options: [
        { text: { zh: "零个", en: "None", vi: "Không có" }, why: { zh: "那是车的走法。炮吃子必须隔子。", en: "That is how a chariot captures. A cannon cannot take anything it has a clear line to.", vi: "Đó là cách Xe ăn quân. Pháo không ăn được quân nào nằm trên đường thông thẳng tới nó." } },
        { text: { zh: "正好一个", en: "Exactly one", vi: "Đúng một" }, ok: true, why: { zh: "对。不多不少，正好一个，颜色不限。", en: "Right. Exactly one, no more and no fewer, and it can be either player's piece.", vi: "Đúng. Đúng một quân, không hơn không kém, và là quân của bên nào cũng được." } },
        { text: { zh: "一个或更多", en: "One or more", vi: "Một hoặc nhiều hơn" }, why: { zh: "两个就不行了。多一个棋子挡在中间，就是完整的防守。", en: "Two is already too many. Adding a second piece to the line is a complete defence against the cannon.", vi: "Hai quân là đã quá nhiều. Thêm quân thứ hai vào đường đó là cách phòng thủ trọn vẹn trước Pháo." } },
      ],
    },
    next: ["b-horse-block", "t-fork-horse"],
  },

  {
    id: "r-flying-general",
    group: G_RULES,
    stage: "beginner",
    minutes: 4,
    title: { zh: "白脸将 · 将帅不见面", en: "The Flying General", vi: "Lộ mặt tướng" },
    fen: "4k4/9/9/9/9/9/9/9/4R4/4K4 w - - 0 1",
    focus: "e1",
    text: {
      zh: "两个将帅永远不能在同一条直线上照面，中间没有任何棋子。所以 e1 的车其实被「绝对牵制」了：任何让它离开 e 线的走法都不合法。试着把它横着走走看。",
      en: "The two generals may never face each other down a file with nothing between them. That makes the chariot on e1 absolutely pinned — not by an attacker, but by the rule itself. Try to move it sideways and see.",
      vi: "Hai Tướng không bao giờ được đối mặt nhau trên một lộ mà ở giữa không có quân nào. Vì vậy Xe ở e1 bị ghim tuyệt đối — không phải bởi quân tấn công, mà bởi chính luật. Thử đi ngang con Xe xem.",
    },
    // No `misses` here on purpose: every sideways move is *illegal*, so the
    // board refuses it outright and it never reaches the lesson as a wrong
    // answer. The refusal is the lesson.
    task: { kind: "play-move", solution: ["e1e2", "e1e3", "e1e4", "e1e5", "e1e6", "e1e7", "e1e8"] },
    success: {
      zh: "对。车只能在 e 线上活动，因为它是唯一挡在两将之间的棋子。",
      en: "Exactly. The chariot may only travel along the e-file, because it is the only thing standing between the two generals.",
      vi: "Chính xác. Xe chỉ được đi dọc lộ e, vì nó là quân duy nhất đứng giữa hai Tướng.",
    },
    quiz: {
      q: { zh: "白脸将规则对同一横线上的两将适用吗？", en: "Does the flying general rule apply to generals on the same rank?", vi: "Luật lộ mặt tướng có áp dụng khi hai Tướng cùng hàng ngang không?" },
      options: [
        { text: { zh: "适用", en: "Yes", vi: "Có" }, why: { zh: "不适用。只限于直线（纵线）。", en: "No — the rule covers files only.", vi: "Không — luật chỉ áp dụng cho lộ dọc." } },
        { text: { zh: "只限纵线", en: "Files only", vi: "Chỉ lộ dọc" }, ok: true, why: { zh: "对。规则只管纵线。两将本来就不会在同一横线上：两个九宫分在棋盘两端。", en: "Correct. The rule covers files only. The two generals can never share a rank anyway: their palaces are at opposite ends of the board.", vi: "Đúng. Luật chỉ xét lộ dọc. Hai Tướng vốn không bao giờ cùng một hàng ngang: hai cung nằm ở hai đầu bàn cờ." } },
      ],
    },
    next: ["r-check", "r-mate"],
  },

  {
    id: "t-cannon-mate",
    group: G_TACTICS,
    stage: "intermediate",
    minutes: 5,
    title: { zh: "炮的杀法 · 借子成杀", en: "Mating With a Cannon", vi: "Chiếu bí bằng Pháo" },
    fen: "3k5/9/9/9/R8/9/9/9/3C5/4K4 w - - 0 1",
    text: {
      zh: "炮要将军，同样需要炮架。红炮已在 d 线上对准黑将，却少一个炮架。把车走到 d 线上当炮架——黑将会发现无路可走：d8 被炮封住，e9 因白脸将不能去，c9 出了九宫。",
      en: "A cannon needs a screen to give check, exactly as it needs one to capture. The cannon is already lined up on the d-file with the black general — what is missing is the screen. Move the chariot onto that file to supply it, and the general runs out of squares: d8 is covered by the cannon, e9 is forbidden by the flying general rule, and c9 is outside the palace.",
      vi: "Pháo cần ngòi để chiếu, cũng như cần ngòi để ăn quân. Pháo đã nằm cùng lộ d với Tướng đen — chỉ còn thiếu ngòi. Đưa Xe vào lộ đó làm ngòi, và Tướng hết chỗ chạy: d8 bị Pháo khống chế, e9 bị cấm vì luật lộ mặt tướng, còn c9 nằm ngoài cung.",
    },
    task: { kind: "achieve-mate" },
    success: {
      zh: "将死。车成为炮架，炮沿 d 线将军；而 e9 之所以不能走，正是因为白脸将——两条规则合起来才成杀。",
      en: "Checkmate. The chariot became the screen, the cannon fired down the d-file — and the reason the general cannot simply step to e9 is the flying general rule. Two rules together make the mate.",
      vi: "Chiếu bí. Xe làm ngòi, Pháo bắn dọc lộ d — và lý do Tướng không thể bước sang e9 chính là luật lộ mặt tướng. Hai luật kết hợp lại tạo nên thế chiếu bí.",
    },
    quiz: {
      q: { zh: "「炮架」可以是自己的棋子吗？", en: "Can a cannon use your own piece as its screen?", vi: "Pháo có thể dùng quân của chính bạn làm ngòi không?" },
      options: [
        { text: { zh: "可以，颜色不限", en: "Yes, either colour works", vi: "Có, quân bên nào cũng được" }, ok: true, why: { zh: "对。这也是为什么把自己的子走进炮线会很危险。", en: "Right — and it is why moving your own piece into a cannon's line can be a serious mistake.", vi: "Đúng — và đó là lý do đưa quân mình vào đường Pháo có thể là một sai lầm nghiêm trọng." } },
        { text: { zh: "只能用对方的子", en: "Only the opponent's pieces", vi: "Chỉ quân đối phương" }, why: { zh: "不对。炮架的颜色完全不重要。", en: "Not so. The screen's colour is entirely irrelevant to the rule.", vi: "Không phải. Ngòi là quân bên nào hoàn toàn không quan trọng." } },
      ],
    },
    next: ["t-fork-rook", "e-two-rooks"],
  },
];

export const LESSON_GROUPS = (): { group: Bi; items: Lesson[] }[] => {
  const out: { group: Bi; items: Lesson[] }[] = [];
  for (const l of LESSONS) {
    let g = out.find((x) => x.group.en === l.group.en);
    if (!g) {
      g = { group: l.group, items: [] };
      out.push(g);
    }
    g.items.push(l);
  }
  return out;
};

// ---- progress ----
const KEY = "xq-lessons-done";
export function doneSet(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}
export function markDone(id: string) {
  const s = doneSet();
  s.add(id);
  localStorage.setItem(KEY, JSON.stringify([...s]));
}
